import {
  Exclude,
  instanceToPlain,
  plainToInstance,
  Type,
} from 'class-transformer'
import {
  FolderPath,
  FolderResponseWithExcludedFields,
  IFolderResponse,
} from '@core/entities/folders/FolderResponse'
import { Folder } from '@core/entities/folders/Folder'
import { Entity } from '@core/entities/entity'
import { PermitTemplate } from '@core/entities/permit/PermitTemplate'
import { ChecklistTemplate } from '@core/entities/checklist/ChecklistTemplate'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import _remove from 'lodash/remove'
import { TaskTemplate } from '@core/entities/task/TaskTemplate'
import { ProcoreDocument } from '@core/entities/ProcoreDocument'

export class FolderResponse extends Entity<IFolderResponse> {
  public toPlain(): IFolderResponse {
    const { files, ...base } = instanceToPlain(this)
    base.templates = _remove(
      this.files,
      (file) => file instanceof PermitTemplate,
    )
    base.checklistTemplates = _remove(
      this.files,
      (file) => file instanceof ChecklistTemplate,
    )
    base.taskTemplates = _remove(
      this.files,
      (file) => file instanceof TaskTemplate,
    )
    base.siteBoxDocuments = this.files
    base.procoreDocuments = this.files

    return base as IFolderResponse
  }

  @Type(() => Folder)
  currentFolder: Folder

  @Type(() => Folder)
  folders: Folder[]

  pathFromRoot: FolderPath

  @Exclude()
  templates: PermitTemplate[]

  @Exclude()
  checklistTemplates: ChecklistTemplate[]

  @Exclude()
  taskTemplates: TaskTemplate[]

  @Exclude()
  siteBoxDocuments: SiteBoxDocument[]

  @Exclude()
  procoreDocuments: ProcoreDocument[]

  files: Array<
    PermitTemplate | ChecklistTemplate | SiteBoxDocument | ProcoreDocument
  >

  public static new(
    payload: IFolderResponse,
  ): FolderResponseWithExcludedFields {
    const entity = plainToInstance(FolderResponse, {
      ...payload,
      files: [
        ...(payload.templates || []).map((template) =>
          PermitTemplate.new(template),
        ),
        ...(payload.checklistTemplates || []).map((template) =>
          ChecklistTemplate.new(template),
        ),
        // INSPECTION_TEMPLATE folders return their templates here; they are
        // checklist templates, so they join the same `files` list.
        ...(payload.inspectionTemplates || []).map((template) =>
          ChecklistTemplate.new(template),
        ),
        ...(payload.taskTemplates || []).map((template) =>
          TaskTemplate.new(template),
        ),
        ...(payload.siteBoxDocuments || []).map((document) =>
          SiteBoxDocument.new(document),
        ),
        ...(payload.procoreDocuments || []).map((document) =>
          ProcoreDocument.new(document),
        ),
      ],
    })

    return entity
  }

  public isRootFolder(): boolean {
    return this.currentFolder.displayName === 'ROOT'
  }

  public getFolderId(): number {
    return this.currentFolder.id
  }

  public getFolderName(): string {
    return this.currentFolder.displayName
  }

  public getParentFolderId(): number {
    return this.currentFolder.parentId
  }
}
